import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import HelpTip from '@/components/HelpTip';
import { Check, X, RefreshCw, MessageSquareQuote } from 'lucide-react';

/**
 * Aprobación humana de FAQs aprendidas de conversaciones.
 * Solo propuestas con aprobado=false y no descartadas.
 * Al aprobar se vuelca a knowledge_faqs del agente.
 */
export default function FaqPropuestasPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // pregunta en curso
  const [aprendidas, setAprendidas] = useState([]);
  const [negra, setNegra] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a, n] = await Promise.all([
        api.get('/ai-faq-propuestas'),
        api.get('/ai-faq-propuestas/aprendidas'),
        api.get('/ai-faq-propuestas/lista-negra').catch(() => ({ data: { items: [] } })),
      ]);
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setAprendidas(Array.isArray(a.data?.items) ? a.data.items : []);
      setNegra(Array.isArray(n.data?.items) ? n.data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar las propuestas');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const aprobar = async (pregunta) => {
    setBusy(pregunta);
    try {
      await api.post('/ai-faq-propuestas/aprobar', { pregunta, volcar_a_knowledge: true });
      toast.success('FAQ aprobada y agregada al conocimiento del agente');
      setItems((prev) => prev.filter((x) => x.pregunta !== pregunta));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo aprobar');
    } finally {
      setBusy(null);
    }
  };


  const quitar = async (pregunta) => {
    if (!window.confirm('¿Quitar del conocimiento? No se volverá a aprender (lista negra).')) return;
    setBusy(pregunta);
    try {
      await api.post('/ai-faq-propuestas/quitar', { pregunta });
      toast.success('FAQ quitada y en lista negra');
      setAprendidas((prev) => prev.filter((x) => x.pregunta !== pregunta));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo quitar');
    } finally {
      setBusy(null);
    }
  };

  const rechazar = async (pregunta) => {
    setBusy(pregunta);
    try {
      await api.post('/ai-faq-propuestas/rechazar', { pregunta });
      toast.success('Propuesta descartada');
      setItems((prev) => prev.filter((x) => x.pregunta !== pregunta));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo descartar');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4 space-y-3"
      data-testid="faq-propuestas-panel"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-white font-semibold flex items-center gap-1.5">
            <MessageSquareQuote className="w-3.5 h-3.5 text-amber-400" />
            FAQs propuestas (aprendidas de conversaciones)
            <HelpTip
              title="FAQs propuestas"
              content={
                "El sistema revisa conversaciones reales y sugiere preguntas frecuentes.\n\n" +
                "El ciclo diario (auto-ml) vuelca lo aprendido solo. Tú PODAS lo que no quieras.\n\n" +
                "Al quitar, entra a lista negra y no se vuelve a aprender.\n\n" +
                "Las propuestas pendientes siguen pudiendo aprobarse a mano si aparecen."
              }
            />
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Pendientes de tu revisión. Solo administradores.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={load}
          disabled={loading}
          data-testid="faq-propuestas-refresh"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {loading && (
        <p className="text-[11px] text-muted-foreground">Cargando propuestas…</p>
      )}

      {!loading && items.length === 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="faq-propuestas-empty">
          No hay propuestas pendientes. Cuando el aprendizaje corra, aparecerán aquí.
        </p>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {items.map((it) => (
          <div
            key={it.pregunta}
            className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2"
            data-testid="faq-propuesta-item"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              {(it.veces || 0) > 0 && (
                <Badge variant="outline" className="text-[10px] border-white/20">
                  {it.veces}×
                </Badge>
              )}
              {it.por_humano && (
                <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-0">
                  Contestó humano
                </Badge>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Pregunta</p>
              <p className="text-xs text-white whitespace-pre-wrap">{it.pregunta}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Respuesta propuesta</p>
              <p className="text-xs text-white/90 whitespace-pre-wrap">{it.respuesta}</p>
            </div>
            {it.ejemplo && (
              <p className="text-[10px] text-muted-foreground italic">Ejemplo: {it.ejemplo}</p>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500"
                disabled={busy === it.pregunta}
                onClick={() => aprobar(it.pregunta)}
                data-testid="faq-aprobar"
              >
                <Check className="w-3 h-3 mr-1" /> Aprobar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs border-white/15"
                disabled={busy === it.pregunta}
                onClick={() => rechazar(it.pregunta)}
                data-testid="faq-rechazar"
              >
                <X className="w-3 h-3 mr-1" /> Descartar
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-3 border-t border-white/10 space-y-2" data-testid="faq-aprendidas-panel">
        <p className="text-xs text-white font-semibold">Aprendido (puedes quitar)</p>
        <p className="text-[10px] text-muted-foreground">
          Volcado por auto-ml o aprobación manual. Quitar = sale del conocimiento + lista negra.
        </p>
        {aprendidas.length === 0 && (
          <p className="text-[11px] text-muted-foreground">Aún no hay FAQs aprendidas volcadas.</p>
        )}
        {aprendidas.slice(0, 30).map((it) => (
          <div key={it.pregunta} className="rounded-lg border border-white/10 bg-black/20 p-2 space-y-1" data-testid="faq-aprendida-row">
            <div className="flex justify-between gap-2">
              <p className="text-[11px] text-white font-medium">{it.pregunta}</p>
              <Badge variant="outline" className="text-[9px] shrink-0">{it.revisado_por || '—'}</Badge>
            </div>
            <p className="text-[10px] text-muted-foreground line-clamp-2">{it.respuesta}</p>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-rose-300" disabled={busy === it.pregunta} onClick={() => quitar(it.pregunta)} data-testid="faq-quitar-btn">
              Quitar
            </Button>
          </div>
        ))}
        {negra.length > 0 && (
          <p className="text-[10px] text-muted-foreground" data-testid="faq-lista-negra-count">
            Lista negra: {negra.length} (no se re-aprenden)
          </p>
        )}
      </div>
    </div>
  );
}
